import type { ReactNode } from "react";

import {
  ART_PROVIDER,
  ArtProviderLogo,
} from "@/components/ArtAttributionBadge";
import {
  MONOLITH_BOARD_STYLE,
  getButtonClassName,
} from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";

function CreditsSection({
  eyebrow,
  title,
  children,
  className,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("p-6 sm:p-8", MONOLITH_BOARD_STYLE.panelClassName, className)}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-neutral-600">
        {eyebrow}
      </p>
      <h2 className="mt-4 text-2xl font-semibold tracking-[-0.04em] text-neutral-950 sm:text-3xl">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-sm leading-7 text-neutral-800 sm:text-base">
        {children}
      </div>
    </section>
  );
}

export default function CreditsPage() {
  return (
    <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
      <div className="absolute inset-x-0 top-0 h-32 bg-[linear-gradient(180deg,rgba(19,19,19,0.05),transparent)]" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-6 sm:px-6 lg:px-10">
        <div className="flex justify-start">
          <Button
            variant="secondary"
            className="min-w-0 px-5 text-center"
            onClick={() => window.history.back()}
          >
            Back to map
          </Button>
        </div>

        <header
          className={cn(
            "mt-6 flex flex-col gap-8 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10",
            MONOLITH_BOARD_STYLE.dashboardHeaderPanelClassName,
          )}
        >
          <div className="min-w-0 max-w-3xl flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-neutral-600">
              Art credits
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <h1 className="min-w-0 text-4xl font-semibold tracking-[-0.05em] text-neutral-950 sm:text-5xl lg:text-6xl">
                {ART_PROVIDER.name}
              </h1>
              <ArtProviderLogo
                className="h-12 w-auto max-w-48 shrink-0 object-contain sm:h-14 lg:h-16 lg:max-w-56"
                fallbackClassName="min-h-12 min-w-28 border-[3px] border-neutral-950 px-3 text-sm text-neutral-950 sm:min-h-14 sm:text-base lg:min-h-16"
              />
            </div>
            <p className="mt-4 text-base leading-relaxed text-neutral-800 sm:text-lg">
              {ART_PROVIDER.name} has kindly granted us permission to use their art
              assets in the app. Most hex tiles, overlays, and
              decorations on your map come from their hand-drawn work — an enormous creative
              contribution that gives the app its visual identity. If their art has helped your world
              come alive, please return the favor: visit their site, buy a pack, or join their Patreon.
            </p>
          </div>

          <div className="flex w-full shrink-0 flex-col border-t border-neutral-950/10 pt-6 lg:max-w-60 lg:justify-center lg:border-t-0 lg:border-l lg:border-neutral-950/10 lg:pl-10 lg:pt-0">
            <a
              href={ART_PROVIDER.url}
              target="_blank"
              rel="noopener noreferrer"
              className={getButtonClassName({
                variant: "primary",
                className: "w-full min-w-0 px-5 text-center lg:w-full",
              })}
            >
              Visit 2minutetabletop.com
            </a>
          </div>
        </header>

        <main className="mt-6">
          <CreditsSection eyebrow="About" title={`About ${ART_PROVIDER.name}`}>
            <p>
              {ART_PROVIDER.name} is a tabletop art studio known for warm, readable fantasy map
              assets that are built for play. Their packs make it easy to assemble maps that feel
              generous, tactile, and immediately usable at the table.
            </p>
            <p>
              Their work is central to the look and feel of westmarches.co. We are grateful for
              their permission to use their assets in the app, and we want that contribution to be
              easy to see and easy to follow back to the source.
            </p>
          </CreditsSection>
        </main>

        <footer className="mt-8 pb-4 text-center text-[11px] uppercase tracking-[0.24em] text-neutral-600">
          artwork by {ART_PROVIDER.name}.
        </footer>
      </div>
    </div>
  );
}
